import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Download, Star, TrendingUp, Users, Target, FileText, ExternalLink, ChevronRight, X, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ─── DESIGN TOKENS ─────────────────────────────────────────
const FONT = { heading: "'Manrope', sans-serif", body: "'IBM Plex Sans', sans-serif", data: "'JetBrains Mono', monospace" };
const COLOR = { navy: '#0f172a', accent: '#2563eb', success: '#10b981', warning: '#f59e0b', error: '#ef4444', border: '#e2e8f0', surface: '#f8fafc' };

const getStatusColor = (pct) => pct >= 100 ? COLOR.success : pct >= 60 ? COLOR.warning : COLOR.error;
const getStatusBorder = (status) => status === 'exceeded' ? 'border-l-emerald-500' : status === 'on_track' ? 'border-l-amber-500' : 'border-l-red-400';
const getStatusBg = (status) => status === 'exceeded' ? 'bg-emerald-50 text-emerald-700' : status === 'on_track' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700';
const getStatusLabel = (status) => status === 'exceeded' ? 'Target Exceeded' : status === 'on_track' ? 'On Track' : 'Below Target';

// ─── UTILITIES ─────────────────────────────────────────────
const formatCurrency = (value) => {
  if (!value) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN')}`;
};
const formatNumber = (value) => { if (!value) return '0'; return Math.round(value).toLocaleString('en-IN'); };

// ─── STAR RATING ───────────────────────────────────────────
function StarRating({ rating, size = "md" }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const sizeClass = size === "lg" ? "w-5 h-5" : "w-3.5 h-3.5";
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} className={`${sizeClass} ${n <= fullStars ? 'fill-amber-400 text-amber-400' : n === fullStars + 1 && hasHalf ? 'fill-amber-400/50 text-amber-400' : 'text-slate-300'}`} />
      ))}
    </div>
  );
}

// ─── TREND INDICATOR ───────────────────────────────────────
function TrendIndicator({ current, previous }) {
  if (!previous || previous === 0) return null;
  const change = ((current - previous) / previous) * 100;
  if (Math.abs(change) < 0.5) return null;
  const isUp = change > 0;
  return <span className={`text-[10px] font-semibold ${isUp ? 'text-emerald-600' : 'text-red-500'}`} style={{ fontFamily: FONT.data }}>{isUp ? '+' : ''}{change.toFixed(0)}%</span>;
}

// ─── ACHIEVEMENT BAR ───────────────────────────────────────
function AchievementBar({ percent, label }) {
  const pct = Math.min(percent || 0, 150);
  const color = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div>
      {label && <div className="flex justify-between mb-1"><span className="text-[10px] text-slate-400">{label}</span><span className="text-[10px] font-semibold text-slate-700" style={{ fontFamily: FONT.data }}>{pct.toFixed(1)}%</span></div>}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

// ─── MONTHLY CARD ──────────────────────────────────────────
function MonthlyCard({ data, onClick, visibleColumns = [], prevMonthData = null }) {
  const allFields = {
    "Total Hot Leads Given in Current Month": { label: "Hot Leads", value: formatNumber(data.hot_leads), rawValue: data.hot_leads, prevValue: prevMonthData?.hot_leads },
    "Total Active Hot Leads": { label: "Active Leads", value: formatNumber(data.active_leads || 0), rawValue: data.active_leads || 0, prevValue: prevMonthData?.active_leads },
    "Total Invoice Sales with Tax Achieved with GST": { label: "Achieved Sales", value: formatCurrency(data.achieved_sales), rawValue: data.achieved_sales, prevValue: prevMonthData?.achieved_sales, isCurrency: true },
    "Expected Invoice Sales with Tax Target with GST": { label: "Target Sales", value: formatCurrency(data.target_sales), rawValue: data.target_sales, prevValue: prevMonthData?.target_sales },
    "Achievement of Revenue %": { label: "Achievement %", value: `${data.achievement_percent?.toFixed(1)}%`, rawValue: data.achievement_percent, prevValue: prevMonthData?.achievement_percent, isPercent: true },
    "Conversion %": { label: "Conversion Rate", value: `${data.conversion_percent?.toFixed(1)}%`, rawValue: data.conversion_percent, prevValue: prevMonthData?.conversion_percent },
    "Total Payment Received with GST": { label: "Payment Received", value: formatCurrency(data.payment_received), rawValue: data.payment_received, prevValue: prevMonthData?.payment_received, isCurrency: true },
    "Achievement % of Payment Received vs Total Revenue Actual": { label: "Payment Efficiency", value: `${data.payment_efficiency?.toFixed(1)}%`, rawValue: data.payment_efficiency, prevValue: prevMonthData?.payment_efficiency },
    "No. of Invoices": { label: "Invoices", value: formatNumber(data.num_invoices || 0), rawValue: data.num_invoices || 0, prevValue: prevMonthData?.num_invoices },
    "Avg Value of Invoice": { label: "Avg Invoice", value: formatCurrency(data.avg_invoice || 0), rawValue: data.avg_invoice || 0, prevValue: prevMonthData?.avg_invoice },
    "Overall Rating": { label: "Rating", value: <StarRating rating={data.rating || 0} />, rawValue: data.rating, prevValue: prevMonthData?.rating },
    "Feedback": { label: "Feedback", value: data.feedback || 'N/A', isText: true },
    "Any Employee Concerns": { label: "Concerns", value: data.concerns || 'None', isText: true }
  };
  
  const defaultFields = ["Total Hot Leads Given in Current Month", "Total Invoice Sales with Tax Achieved with GST", "Expected Invoice Sales with Tax Target with GST", "Achievement of Revenue %", "Conversion %", "Total Payment Received with GST", "Feedback", "Any Employee Concerns"];
  const fieldsToShow = visibleColumns.length > 0 ? visibleColumns : defaultFields;
  const textFields = ["Feedback", "Any Employee Concerns"];
  const metricFields = fieldsToShow.filter(f => !textFields.includes(f));
  const textFieldsToShow = fieldsToShow.filter(f => textFields.includes(f));
  
  return (
    <div onClick={onClick} className={`bg-white border border-slate-200 border-l-4 ${getStatusBorder(data.status)} rounded-sm overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200`} data-testid={`month-card-${data.audit_month}`}>
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide" style={{ fontFamily: FONT.heading }}>{data.audit_month?.replace('-', ' ')}</h3>
        <span className={`text-[9px] font-semibold uppercase px-2 py-1 rounded-sm ${getStatusBg(data.status)}`}>{getStatusLabel(data.status)}</span>
      </div>
      
      {/* Achievement Bar */}
      <div className="px-5 pt-4 pb-2">
        <AchievementBar percent={data.achievement_percent} label="Revenue Achievement" />
      </div>
      
      {/* Metrics */}
      <div className="px-5 pb-3 space-y-0">
        {metricFields.map(fieldKey => {
          const field = allFields[fieldKey];
          if (!field || fieldKey === "Achievement of Revenue %") return null;
          return (
            <div key={fieldKey} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
              <span className="text-[11px] text-slate-500" style={{ fontFamily: FONT.body }}>{field.label}</span>
              <div className="flex items-center gap-1.5">
                <span className={`text-xs font-semibold ${field.isCurrency ? 'text-slate-900' : 'text-slate-700'}`} style={{ fontFamily: FONT.data }}>{field.value}</span>
                {field.prevValue !== undefined && !field.isText && <TrendIndicator current={field.rawValue} previous={field.prevValue} />}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Text Fields */}
      {textFieldsToShow.length > 0 && (
        <div className="px-5 pb-4 space-y-2">
          {textFieldsToShow.map(fieldKey => {
            const field = allFields[fieldKey];
            if (!field) return null;
            return (
              <div key={fieldKey}>
                <p className="text-[10px] uppercase text-slate-400 mb-0.5" style={{ fontFamily: FONT.body }}>{field.label}</p>
                <p className="text-[11px] text-slate-600 italic leading-snug">{field.value}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── SUMMARY SECTION ───────────────────────────────────────
function PeriodSummary({ summary, period, fy, quarter }) {
  const periodLabel = period === 'quarter' ? `Q${quarter} FY ${fy}` : period === 'year' ? `FY ${fy}` : 'Selected Period';
  const metrics = [
    { label: 'Total Leads', value: formatNumber(summary.total_leads), icon: Users },
    { label: 'Revenue Achieved', value: formatCurrency(summary.total_achieved), icon: TrendingUp, highlight: true },
    { label: 'Revenue Target', value: formatCurrency(summary.total_target), icon: Target },
    { label: 'Achievement', value: `${summary.overall_achievement_percent?.toFixed(1)}%`, color: getStatusColor(summary.overall_achievement_percent) },
    { label: 'Payment Efficiency', value: `${summary.payment_efficiency?.toFixed(1)}%`, icon: FileText },
    { label: 'Avg Conversion', value: `${summary.avg_conversion?.toFixed(1)}%` },
  ];
  
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-900">
        <h3 className="text-sm font-bold text-white uppercase tracking-wide" style={{ fontFamily: FONT.heading }}>{period === 'quarter' ? 'Quarterly' : period === 'year' ? 'Annual' : 'Period'} Summary</h3>
        <p className="text-[10px] text-slate-400 mt-0.5">{periodLabel}</p>
      </div>
      <div className="p-5 grid grid-cols-3 md:grid-cols-6 gap-4">
        {metrics.map((m, i) => (
          <div key={i} className="text-center">
            <p className="text-[9px] uppercase text-slate-400 mb-1.5 tracking-wider" style={{ fontFamily: FONT.body }}>{m.label}</p>
            <p className={`text-lg font-bold ${m.highlight ? 'text-slate-900' : 'text-slate-700'}`} style={{ fontFamily: FONT.data, color: m.color }}>{m.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── INSIGHTS + RATING ─────────────────────────────────────
function InsightsPanel({ insights, rating, ratingText }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3" style={{ fontFamily: FONT.body }}>Key Insights</h4>
        <ul className="space-y-2">
          {insights?.map((ins, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-slate-600"><span className="w-1.5 h-1.5 rounded-full bg-slate-900 mt-1.5 flex-shrink-0" />{ins}</li>
          ))}
          {(!insights || insights.length === 0) && <li className="text-xs text-slate-400 italic">No insights available</li>}
        </ul>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col items-center justify-center">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3" style={{ fontFamily: FONT.body }}>Overall Rating</h4>
        <StarRating rating={rating || 0} size="lg" />
        <p className="mt-2 text-xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{rating?.toFixed(1)} <span className="text-slate-400 text-sm">/ 5</span></p>
        <p className="text-xs text-slate-500 mt-1 italic">{ratingText}</p>
      </div>
    </div>
  );
}

// ─── SUBMISSION DETAIL PANEL ───────────────────────────────
function SubmissionDetailPanel({ submissionId, formId, isOpen, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && submissionId) {
      setLoading(true);
      api.get(`/management-dashboard/${formId}/submission/${submissionId}`)
        .then(res => setDetail(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen, submissionId, formId]);
  
  if (!isOpen) return null;
  const MetricRow = ({ label, value, highlight }) => (
    <div className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${highlight || 'text-slate-900'}`} style={{ fontFamily: FONT.data }}>{value}</span>
    </div>
  );
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[480px] sm:max-w-[480px] bg-white overflow-y-auto">
        <SheetHeader><SheetTitle className="text-slate-900 flex items-center gap-2" style={{ fontFamily: FONT.heading }}><FileText className="w-5 h-5" />Submission Details</SheetTitle></SheetHeader>
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" /></div>
        ) : detail ? (
          <div className="mt-6 space-y-5">
            <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Period</h4>
              <div className="grid grid-cols-2 gap-3 text-sm"><div><span className="text-slate-400 text-xs">Month:</span><p className="font-semibold text-slate-900" style={{ fontFamily: FONT.data }}>{detail.timing?.audit_month}</p></div><div><span className="text-slate-400 text-xs">Filed:</span><p className="font-semibold text-slate-900" style={{ fontFamily: FONT.data }}>{detail.timing?.date_of_filing}</p></div></div>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Leads</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><p className="text-[10px] text-slate-400">Hot</p><p className="text-xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{formatNumber(detail.leads?.hot_leads)}</p></div>
                <div><p className="text-[10px] text-slate-400">Active</p><p className="text-xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{formatNumber(detail.leads?.active_leads)}</p></div>
                <div><p className="text-[10px] text-slate-400">Conversion</p><p className="text-xl font-bold" style={{ fontFamily: FONT.data, color: getStatusColor(detail.leads?.conversion_percent || 0) }}>{detail.leads?.conversion_percent?.toFixed(1)}%</p></div>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Sales</h4>
              <MetricRow label="Achieved" value={formatCurrency(detail.sales?.achieved)} highlight="text-emerald-600" />
              <MetricRow label="Target" value={formatCurrency(detail.sales?.target)} />
              <MetricRow label="Achievement" value={`${detail.sales?.achievement_percent?.toFixed(1)}%`} highlight={detail.sales?.achievement_percent >= 100 ? 'text-emerald-600' : 'text-amber-600'} />
              <MetricRow label="Invoices" value={formatNumber(detail.sales?.num_invoices)} />
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Payments</h4>
              <MetricRow label="Received" value={formatCurrency(detail.payments?.received)} highlight="text-blue-600" />
              <MetricRow label="Efficiency" value={`${detail.payments?.efficiency_percent?.toFixed(1)}%`} />
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
              <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Performance</h4>
              <div className="flex items-center justify-between py-2"><span className="text-xs text-slate-500">Rating</span><StarRating rating={detail.performance?.overall_rating || 0} /></div>
              {detail.performance?.feedback && <div className="mt-2"><p className="text-[10px] uppercase text-slate-400">Feedback</p><p className="text-xs text-slate-700 mt-1">{detail.performance.feedback}</p></div>}
              {detail.performance?.concerns && <div className="mt-2"><p className="text-[10px] uppercase text-slate-400">Concerns</p><p className="text-xs text-amber-600 mt-1">{detail.performance.concerns}</p></div>}
            </div>
          </div>
        ) : <div className="text-center text-slate-400 mt-8">No data available</div>}
      </SheetContent>
    </Sheet>
  );
}

// ─── SETTINGS TAB ──────────────────────────────────────────
function SettingsTab({ formId, config, onSave }) {
  const [teamLead, setTeamLead] = useState({ name: 'Pooja', emp_code: 'TL-1024', email: 'pooja@company.com', photo_url: '' });
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (config?.team_lead) setTeamLead(config.team_lead);
    if (config?.visible_columns) setVisibleColumns(config.visible_columns);
  }, [config]);
  
  const allColumns = ["Total Hot Leads Given in Current Month", "Total Active Hot Leads", "Total Invoice Sales with Tax Achieved with GST", "Expected Invoice Sales with Tax Target with GST", "Achievement of Revenue %", "Conversion %", "Total Payment Received with GST", "Achievement % of Payment Received vs Total Revenue Actual", "No. of Invoices", "Avg Value of Invoice", "Overall Rating", "Feedback", "Any Employee Concerns"];
  
  const handleSave = async () => {
    setSaving(true);
    try { await api.put(`/management-dashboard/${formId}/config`, { team_lead: teamLead, visible_columns: visibleColumns, fy_start_month: 4 }); onSave?.(); }
    catch (err) { console.error('Error saving config:', err); }
    finally { setSaving(false); }
  };
  
  const toggleColumn = (col) => setVisibleColumns(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
  
  return (
    <div className="space-y-6 p-6">
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: FONT.heading }}><Users className="w-4 h-4 text-slate-500" />Team Lead Configuration</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs text-slate-500">Name</Label><Input value={teamLead.name} onChange={(e) => setTeamLead(prev => ({ ...prev, name: e.target.value }))} className="mt-1 rounded-sm" /></div>
          <div><Label className="text-xs text-slate-500">Emp Code</Label><Input value={teamLead.emp_code} onChange={(e) => setTeamLead(prev => ({ ...prev, emp_code: e.target.value }))} className="mt-1 rounded-sm" /></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Email</Label><Input value={teamLead.email} onChange={(e) => setTeamLead(prev => ({ ...prev, email: e.target.value }))} className="mt-1 rounded-sm" /></div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: FONT.heading }}><Settings className="w-4 h-4 text-slate-500" />Visible Columns on Monthly Cards</h3>
        <div className="grid grid-cols-2 gap-3">
          {allColumns.map(col => (
            <label key={col} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer hover:text-slate-900">
              <Checkbox checked={visibleColumns.includes(col)} onCheckedChange={() => toggleColumn(col)} className="rounded-sm" /><span className="truncate">{col}</span>
            </label>
          ))}
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving} className="w-full rounded-sm">{saving ? 'Saving...' : 'Save Settings'}</Button>
    </div>
  );
}

// ─── ERROR BOUNDARY ────────────────────────────────────────
class DashboardErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center p-8"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" /><h2 className="text-lg font-bold text-slate-900 mb-2">Dashboard Error</h2><p className="text-sm text-slate-500 mb-4">Something went wrong.</p><Button onClick={() => window.location.reload()} className="rounded-sm">Reload</Button></div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── MAIN DASHBOARD ────────────────────────────────────────
function ManagementDashboardInner() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const dashboardRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [initialLoad, setInitialLoad] = useState(true);
  const [period, setPeriod] = useState('quarter');
  const [selectedFY, setSelectedFY] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  
  const loadConfig = useCallback(() => {
    api.get(`/management-dashboard/${formId}/config`).then(res => setConfig(res.data)).catch(() => {});
  }, [formId]);
  
  useEffect(() => { loadConfig(); }, [loadConfig]);
  
  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ period });
      if (selectedFY) params.append('fy', selectedFY);
      if (period === 'quarter' && selectedQuarter > 0) params.append('quarter', selectedQuarter);
      if (period === 'month' && selectedMonth) params.append('month', selectedMonth);
      const res = await api.get(`/management-dashboard/${formId}/overview?${params}`);
      setData(res.data);
      if (initialLoad && res.data) {
        if (!selectedFY && res.data.available_fys?.length > 0) setSelectedFY(res.data.available_fys[0]);
        if (period === 'quarter' && res.data.quarter) setSelectedQuarter(res.data.quarter);
        else if (selectedQuarter === 0) setSelectedQuarter(1);
        setInitialLoad(false);
      }
    } catch (err) { setError(err.response?.data?.detail || 'Failed to load data'); }
    finally { setLoading(false); }
  }, [formId, period, selectedFY, selectedQuarter, selectedMonth, initialLoad]);
  
  useEffect(() => { loadData(); }, [loadData]);
  
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth(), pdfH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pdfW / canvas.width, pdfH / canvas.height);
      pdf.addImage(imgData, 'PNG', (pdfW - canvas.width * ratio) / 2, 0, canvas.width * ratio, canvas.height * ratio);
      pdf.save(`management-dashboard-${selectedFY || 'report'}.pdf`);
    } catch (err) { console.error('PDF error:', err); }
  };
  
  const getQuarterLabel = (q, fy) => {
    const quarters = { 1: 'Apr - Jun', 2: 'Jul - Sep', 3: 'Oct - Dec', 4: 'Jan - Mar' };
    return `Q${q} FY ${fy} (${quarters[q]})`;
  };
  
  if (loading && !data) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-400 mx-auto" /><p className="text-slate-400 text-sm mt-3">Loading dashboard...</p></div></div>;
  }
  
  if (error && !data) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="text-center p-8"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" /><h2 className="text-lg font-bold text-slate-900 mb-2">Unable to Load</h2><p className="text-sm text-slate-500 mb-4">{error}</p><div className="flex gap-3 justify-center"><Button onClick={loadData} className="rounded-sm">Retry</Button><Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-sm">Go Back</Button></div></div></div>;
  }
  
  return (
    <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: FONT.body }}>
      {/* Top Nav */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-900 rounded-sm"><ArrowLeft className="w-4 h-4 mr-2" />Back to Command Center</Button>
        <Button variant="outline" onClick={exportToPDF} className="rounded-sm border-slate-200 text-slate-600"><Download className="w-4 h-4 mr-2" />Export PDF</Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6">
            <TabsList className="bg-transparent border-b-0 h-10">
              <TabsTrigger value="dashboard" className="rounded-sm data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 text-slate-500">Dashboard</TabsTrigger>
              <TabsTrigger value="settings" className="rounded-sm data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 text-slate-500"><Settings className="w-3.5 h-3.5 mr-1.5" />Settings</TabsTrigger>
            </TabsList>
          </div>
        </div>
        
        <TabsContent value="dashboard" className="mt-0">
          <div ref={dashboardRef} className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="bg-slate-900 rounded-sm p-6 text-center">
              <h1 className="text-xl font-bold text-white uppercase tracking-widest" style={{ fontFamily: FONT.heading }}>Management Monthly Sales Audit Dashboard</h1>
              <p className="text-slate-400 text-xs mt-2" style={{ fontFamily: FONT.data }}>
                {period === 'quarter' && selectedFY ? getQuarterLabel(selectedQuarter, selectedFY) : period === 'year' && selectedFY ? `FY ${selectedFY}` : period === 'month' && selectedMonth ? selectedMonth : 'All Data'}
              </p>
            </div>
            
            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">View</span>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-[120px] rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="month" className="text-xs">Monthly</SelectItem><SelectItem value="quarter" className="text-xs">Quarterly</SelectItem><SelectItem value="year" className="text-xs">Annual</SelectItem></SelectContent>
                </Select>
              </div>
              {data?.available_fys?.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">FY</span>
                  <Select value={selectedFY} onValueChange={setSelectedFY}>
                    <SelectTrigger className="w-[110px] rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.available_fys.map(fy => <SelectItem key={fy} value={fy} className="text-xs">{fy}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {period === 'quarter' && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Quarter</span>
                  <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                    <SelectTrigger className="w-[80px] rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4].map(q => <SelectItem key={q} value={String(q)} className="text-xs">Q{q}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {period === 'month' && data?.available_months?.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Month</span>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-[120px] rounded-sm h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{data.available_months.map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Team Lead */}
            {data?.team_lead && (
              <div className="bg-white border border-slate-200 rounded-sm p-5 flex items-center gap-6">
                <div className="w-16 h-16 rounded-sm bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center">
                  {data.team_lead.photo_url ? <img src={data.team_lead.photo_url} alt={data.team_lead.name} className="w-full h-full object-cover" /> : <Users className="w-8 h-8 text-slate-400" />}
                </div>
                <div className="flex-1 grid grid-cols-2 gap-x-8 gap-y-1">
                  <div><span className="text-[10px] uppercase text-slate-400 tracking-wider">Team Lead</span><p className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>{data.team_lead.name}</p></div>
                  <div><span className="text-[10px] uppercase text-slate-400 tracking-wider">Emp Code</span><p className="text-sm text-slate-700" style={{ fontFamily: FONT.data }}>{data.team_lead.emp_code}</p></div>
                  <div className="col-span-2"><span className="text-[10px] uppercase text-slate-400 tracking-wider">Email</span><p className="text-sm text-slate-700" style={{ fontFamily: FONT.data }}>{data.team_lead.email}</p></div>
                </div>
              </div>
            )}
            
            {/* Monthly Cards */}
            {data?.months_data?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.months_data.map((monthData, index) => (
                  <MonthlyCard key={monthData.submission_id} data={monthData} onClick={() => { setSelectedSubmission(monthData.submission_id); setPanelOpen(true); }} visibleColumns={config?.visible_columns || []} prevMonthData={index > 0 ? data.months_data[index - 1] : null} />
                ))}
              </div>
            )}
            
            {(!data?.months_data || data?.months_data?.length === 0) && (
              <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
                <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No data available for the selected period</p>
                {data?.available_quarters?.length > 0 && period === 'quarter' && <p className="text-slate-400 text-xs mt-2">Data available in: {data.available_quarters.map(q => `Q${q}`).join(', ')}</p>}
              </div>
            )}
            
            {/* Summary */}
            {data?.summary && data.months_data?.length > 0 && (
              <div className="space-y-4">
                <PeriodSummary summary={data.summary} period={period} fy={selectedFY} quarter={selectedQuarter} />
                <InsightsPanel insights={data.insights} rating={data.summary?.avg_rating || 0} ratingText={data.rating_text || ''} />
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="settings" className="mt-0">
          <div className="max-w-3xl mx-auto"><SettingsTab formId={formId} config={config} onSave={() => { loadConfig(); loadData(); }} /></div>
        </TabsContent>
      </Tabs>
      
      <SubmissionDetailPanel submissionId={selectedSubmission} formId={formId} isOpen={panelOpen} onClose={() => setPanelOpen(false)} />
    </div>
  );
}

export default function ManagementDashboard() {
  return <DashboardErrorBoundary><ManagementDashboardInner /></DashboardErrorBoundary>;
}
